import React, { useEffect, useState } from "react";
import { ShoppingCart, PaperPlaneTilt, FloppyDisk, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { fetchAutoBuy, updateAutoBuy, testAutoBuy } from "../lib/api";
import { formatApiError } from "../lib/auth";

const FIELDS = [
  ["first_name", "Nombre"], ["last_name", "Apellidos"], ["email", "Email"], ["phone", "Teléfono"],
  ["address_1", "Dirección"], ["city", "Ciudad"], ["postcode", "Código postal"], ["state", "Provincia"],
  ["country", "País (ej: ES)"],
];

export function StockWatchAutoBuy() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchAutoBuy().then(setCfg).catch(() => {}); }, []);

  const save = async (next, msg) => {
    setBusy(true);
    try {
      setCfg(await updateAutoBuy({ enabled: next.enabled, quantity: next.quantity, billing: next.billing }));
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e) || "No se pudo guardar");
    }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      await testAutoBuy();
      toast.success("Notificaciones de prueba (carrito + link de pago) enviadas");
    } catch (e) {
      toast.error(formatApiError(e) || "No se pudo enviar la prueba");
    }
    setBusy(false);
  };

  if (!cfg) return null;
  const setField = (f, v) => setCfg({ ...cfg, billing: { ...cfg.billing, [f]: v } });

  return (
    <div className="border-2 border-zinc-950 p-4 bg-white" data-testid="auto-buy-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#002FA7] flex items-center gap-1">
          <ShoppingCart size={14} weight="bold" /> Auto-compra + link de pago
        </div>
        <label className="flex items-center gap-2 font-mono text-xs uppercase font-bold">
          <span data-testid="auto-buy-status" className={cfg.enabled ? "text-green-700" : "text-zinc-500"}>
            {cfg.enabled ? "Encendido" : "Apagado"}
          </span>
          <Switch
            checked={cfg.enabled}
            disabled={busy}
            onCheckedChange={(v) => save({ ...cfg, enabled: v }, v ? "Auto-compra encendida" : "Auto-compra apagada")}
            data-testid="auto-buy-toggle"
          />
        </label>
      </div>
      <p className="text-xs text-zinc-600 font-mono mb-3">
        Cuando haya stock: agrega al carrito, crea el pedido y te manda por Telegram el link directo de pago (Redsys).
      </p>
      {!cfg.billing_complete && (
        <div className="border border-amber-400 bg-amber-50 text-amber-800 font-mono text-[11px] px-3 py-2 mb-3 flex gap-2" data-testid="auto-buy-billing-warning">
          <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
          Sin tus datos (nombre, apellidos, email, teléfono) solo te enviaré un link de "Compra rápida" de 1 toque.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {FIELDS.map(([f, label]) => (
          <Input key={f} placeholder={label} value={cfg.billing[f] || ""} onChange={(e) => setField(f, e.target.value)}
            className="rounded-none" data-testid={`auto-buy-${f}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={() => save(cfg, "Datos guardados")} disabled={busy} className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-mono uppercase text-xs" data-testid="auto-buy-save">
          <FloppyDisk size={14} weight="bold" className="mr-2" /> Guardar datos
        </Button>
        <Button onClick={test} disabled={busy} variant="outline" className="rounded-none border-[#0088cc] text-[#0088cc] font-mono uppercase text-xs" data-testid="auto-buy-test">
          <PaperPlaneTilt size={14} weight="bold" className="mr-2" /> Probar notificación de pago
        </Button>
      </div>
    </div>
  );
}
